/**
 * Performance Monitoring Utilities
 * Tracks Core Web Vitals and custom performance metrics
 */

import React from 'react';

// Core Web Vitals metrics
export interface PerformanceMetrics {
    // Largest Contentful Paint - loading performance
    LCP?: number;
    // First Input Delay - interactivity
    FID?: number;
    // Cumulative Layout Shift - visual stability
    CLS?: number;
    // First Contentful Paint
    FCP?: number;
    // Time to First Byte
    TTFB?: number;
}

// Custom metrics for app-specific tracking
export interface CustomMetrics {
    routeChangeTime?: number;
    apiResponseTime?: number;
    componentRenderTime?: number;
}

/**
 * Report performance metrics to analytics
 */
const reportMetric = (metric: { name: string; value: number; id?: string }) => {
    // Log in development
    if (process.env.NODE_ENV === 'development') {
        console.log(`[Performance] ${metric.name}:`, metric.value.toFixed(2), 'ms');
    }

    // Send to analytics service in production
    if (process.env.NODE_ENV === 'production') {
        // TODO: Send to Google Analytics, Sentry, or custom analytics
        // Example: ga('send', 'event', 'Performance', metric.name, Math.round(metric.value));
    }
};

/**
 * Measure Core Web Vitals using web-vitals library concepts
 */
export const measureWebVitals = () => {
    // Largest Contentful Paint (LCP)
    const observeLCP = () => {
        const observer = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            const lastEntry = entries[entries.length - 1] as any;
            reportMetric({
                name: 'LCP',
                value: lastEntry.renderTime || lastEntry.loadTime,
            });
            observer.disconnect();
        });

        try {
            observer.observe({ entryTypes: ['largest-contentful-paint'] });
        } catch (e) {
            // LCP not supported
        }
    };

    // First Input Delay (FID)
    const observeFID = () => {
        const observer = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach((entry: any) => {
                reportMetric({
                    name: 'FID',
                    value: entry.processingStart - entry.startTime,
                });
            });
            observer.disconnect();
        });

        try {
            observer.observe({ entryTypes: ['first-input'] });
        } catch (e) {
            // FID not supported
        }
    };

    // Cumulative Layout Shift (CLS)
    const observeCLS = () => {
        let clsValue = 0;
        const observer = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach((entry: any) => {
                if (!entry.hadRecentInput) {
                    clsValue += entry.value;
                }
            });
        });

        try {
            observer.observe({ entryTypes: ['layout-shift'] });
        } catch (e) {
            // CLS not supported
        }

        // Report CLS on page unload
        window.addEventListener('beforeunload', () => {
            reportMetric({
                name: 'CLS',
                value: clsValue,
            });
        });
    };

    // First Contentful Paint (FCP)
    const observeFCP = () => {
        const observer = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach((entry) => {
                reportMetric({
                    name: 'FCP',
                    value: entry.startTime,
                });
            });
            observer.disconnect();
        });

        try {
            observer.observe({ entryTypes: ['paint'] });
        } catch (e) {
            // FCP not supported
        }
    };

    // Initialize all observers
    observeLCP();
    observeFID();
    observeCLS();
    observeFCP();
};

/**
 * Measure custom application metrics
 */
export const measureCustomMetrics = {
    /**
     * Measure route change performance
     */
    routeChange: (routeName: string) => {
        const startTime = performance.now();
        return () => {
            const duration = performance.now() - startTime;
            reportMetric({
                name: `RouteChange: ${routeName}`,
                value: duration,
            });
        };
    },

    /**
     * Measure API call performance
     */
    apiCall: (endpoint: string) => {
        const startTime = performance.now();
        return () => {
            const duration = performance.now() - startTime;
            reportMetric({
                name: `API: ${endpoint}`,
                value: duration,
            });
        };
    },

    /**
     * Measure component render time
     */
    componentRender: (componentName: string) => {
        const startTime = performance.now();
        return () => {
            const duration = performance.now() - startTime;
            reportMetric({
                name: `Render: ${componentName}`,
                value: duration,
            });
        };
    },
};

/**
 * React hook for measuring component performance
 */
export const usePerformanceMonitor = (componentName: string) => {
    const renderStartTime = performance.now();

    React.useEffect(() => {
        const renderTime = performance.now() - renderStartTime;
        if (renderTime > 16) { // Only report if slower than 60fps
            reportMetric({
                name: `Component: ${componentName}`,
                value: renderTime,
            });
        }
    });
};

/**
 * Memory usage monitoring
 */
export const measureMemoryUsage = () => {
    if ('memory' in performance) {
        const memory = (performance as any).memory;
        const usedMemoryMB = memory.usedJSHeapSize / 1048576;
        const totalMemoryMB = memory.totalJSHeapSize / 1048576;

        console.log(`[Memory] Used: ${usedMemoryMB.toFixed(2)} MB / ${totalMemoryMB.toFixed(2)} MB`);

        return {
            used: usedMemoryMB,
            total: totalMemoryMB,
            percentage: (usedMemoryMB / totalMemoryMB) * 100,
        };
    }
    return null;
};

/**
 * Long task monitoring (tasks > 50ms)
 */
export const monitorLongTasks = () => {
    const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
            if (entry.duration > 500) {
                console.warn(`[Long Task] Duration: ${entry.duration.toFixed(2)}ms`);
                reportMetric({
                    name: 'LongTask',
                    value: entry.duration,
                });
            }
        });
    });

    try {
        observer.observe({ entryTypes: ['longtask'] });
    } catch (e) {
        // Long tasks not supported
    }
};

/**
 * Initialize all performance monitoring
 */
export const initPerformanceMonitoring = () => {
    measureWebVitals();
    monitorLongTasks();

    // Report memory usage periodically in development
    if (process.env.NODE_ENV === 'development') {
        setInterval(() => {
            measureMemoryUsage();
        }, 30000); // Every 30 seconds
    }
};
